import { useState, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from './queryClient';

/** Базовые опции, общие для обоих режимов */
type BaseOptions = {
  /** Время актуальности кэша в мс; 0 — всегда перезапрашивать (по умолчанию) */
  staleTime?: number;
  /** Политика повторных попыток при ошибке; `false` — без повторов */
  retry?: boolean | number;
};

/**
 * Опции режима `local`: данные хранятся в локальном `useState` компонента.
 * Используется по умолчанию.
 */
type LocalOptions = BaseOptions & { mode?: 'local' };

/**
 * Опции режима `cached`: данные хранятся в кэше TanStack Query.
 * Реализуется через `useQuery` с `enabled`, запрос запускается через `refetch`.
 * Позволяет читать данные из кэша через `queryClient.getQueryData` или `useQuery` с тем же ключом.
 */
type CachedOptions = BaseOptions & { mode: 'cached' };

/** Тип функции `refetch` — параметр опционален когда `TParams = void` */
type RefetchFn<TParams, TData> = TParams extends void
  ? () => Promise<TData>
  : (params: TParams) => Promise<TData>;

/** Результат хука в режиме `local` */
type LocalHookResult<TParams, TData> = {
  /** Данные последнего успешного запроса; `null` до первого вызова */
  data: TData | null;
  /** Запустить запрос вручную и дождаться результата */
  refetch: RefetchFn<TParams, TData>;
  isLoading: boolean;
  error: Error | null;
};

/** Результат хука в режиме `cached` */
type CachedHookResult<TParams, TData> = {
  /** Данные из кэша TanStack Query; `undefined` до первого вызова */
  data: TData | undefined;
  /** Запустить запрос вручную и дождаться результата */
  refetch: RefetchFn<TParams, TData>;
  isLoading: boolean;
  /** `true` пока идёт фоновое обновление (данные уже есть, но идёт refetch) */
  isFetching: boolean;
  error: Error | null;
};

// Ключ-заглушка для useQuery в disabled-состоянии
const DISABLED_KEY = ['__createLazyQuery__disabled__'] as const;

const createLocalLazyQuery = <TParams, TData>(
  queryKeyFn: (params: TParams) => readonly unknown[],
  queryFn: (params: TParams) => Promise<TData>,
  options?: LocalOptions,
): (() => LocalHookResult<TParams, TData>) =>
  () => {
    const [data, setData] = useState<TData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const refetch = useCallback(async (params: TParams) => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await queryClient.fetchQuery({
          queryKey: queryKeyFn(params),
          queryFn: () => queryFn(params),
          staleTime: options?.staleTime,
          retry: options?.retry,
        });
        setData(result);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    }, []);

    return { data, refetch: refetch as RefetchFn<TParams, TData>, isLoading, error };
  };

const createCachedLazyQuery = <TParams, TData>(
  queryKeyFn: (params: TParams) => readonly unknown[],
  queryFn: (params: TParams) => Promise<TData>,
  options?: CachedOptions,
): (() => CachedHookResult<TParams, TData>) =>
  () => {
    const [params, setParams] = useState<TParams | null>(null);
    // ref нужен, чтобы queryFn всегда видел актуальные params без замыкания на стейт
    const paramsRef = useRef<TParams | null>(null);

    const { data, isLoading, isFetching, error: queryError } = useQuery<TData, Error>({
      queryKey: params !== null ? queryKeyFn(params) : DISABLED_KEY,
      queryFn: () => queryFn(paramsRef.current!),
      enabled: params !== null,
      staleTime: options?.staleTime,
      retry: options?.retry,
    });

    const refetch = useCallback(async (newParams: TParams) => {
      paramsRef.current = newParams;
      setParams(newParams);
      // fetchQuery дедуплицируется с useQuery по ключу — один HTTP-запрос, общий кэш
      return queryClient.fetchQuery({
        queryKey: queryKeyFn(newParams),
        queryFn: () => queryFn(newParams),
        staleTime: options?.staleTime,
        retry: options?.retry,
      });
    }, []);

    return {
      data,
      refetch: refetch as RefetchFn<TParams, TData>,
      isLoading,
      isFetching,
      error: queryError,
    };
  };

/**
 * Фабрика lazy-хуков с кэшированием через React Query.
 *
 * Создаёт хук вида `useLazy*`, который не выполняет запрос автоматически —
 * запрос запускается вручную через `refetch(params)`.
 *
 * ## Режимы
 *
 * - **`local`** (по умолчанию) — данные хранятся в локальном `useState`.
 *   Повторный вызов с теми же `params` возвращает кэш React Query без HTTP-запроса.
 * - **`cached`** — данные хранятся в кэше TanStack Query (`useQuery` + `enabled`).
 *   Данные доступны глобально через `queryClient.getQueryData` или `useQuery` с тем же ключом.
 *   Возвращает дополнительное поле `isFetching`.
 *
 * ## Использование
 *
 * ```ts
 * // Режим local (по умолчанию)
 * export const useLazyUsers = createLazyQuery(
 *   (params) => [...userKeys.list(), params],
 *   fetchUsers,
 * );
 *
 * // Режим cached
 * export const useLazyEmployees = createLazyQuery(
 *   (params) => [...employeeKeys.list(), params],
 *   fetchEmployees,
 *   { mode: 'cached' },
 * );
 *
 * // Без параметров (TParams = void)
 * export const useLazyProfile = createLazyQuery<void, ProfileData>(
 *   () => profileKeys.me(),
 *   () => fetchMyProfile(),
 * );
 * // refetch() вызывается без аргументов
 * ```
 */
export function createLazyQuery<TParams, TData>(
  queryKeyFn: (params: TParams) => readonly unknown[],
  queryFn: (params: TParams) => Promise<TData>,
  options?: LocalOptions,
): () => LocalHookResult<TParams, TData>;

export function createLazyQuery<TParams, TData>(
  queryKeyFn: (params: TParams) => readonly unknown[],
  queryFn: (params: TParams) => Promise<TData>,
  options: CachedOptions,
): () => CachedHookResult<TParams, TData>;

export function createLazyQuery<TParams, TData>(
  queryKeyFn: (params: TParams) => readonly unknown[],
  queryFn: (params: TParams) => Promise<TData>,
  options?: LocalOptions | CachedOptions,
) {
  if (options?.mode === 'cached') {
    return createCachedLazyQuery(queryKeyFn, queryFn, options);
  }
  return createLocalLazyQuery(queryKeyFn, queryFn, options);
}
